import window from 'global/window';
import type {InitOptions} from 'i18next';
import {
	i18n as i18next,
	_setI18nReady,
	_resolveI18nInit,
	_rejectI18nInit,
} from './i18n-instance';

// Re-export instance and state for backwards compatibility
export {i18n, i18nReady, i18nInitialized} from './i18n-instance';

window.i18n = window.i18next = i18next;
// @ts-ignore
if (globalThis) globalThis.i18n = globalThis.i18next = i18next;

import i18nextXHRBackend, {type BackendOptions} from 'i18next-xhr-backend';
import i18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
// @ts-ignore
import jqueryI18next from 'jquery-i18next';
import {getPrefixedLogger} from '../instance/logger';
import {dispatcher} from '../instance/dispatcher';
// importing with full path for webpack/storybook alias support
import {backendOptions} from '../locales/backend';
import {I18N_SUPPORTED_LANGUAGES} from './constants';
import {setPreference} from '@netx/core-user-next/utils/setPreference';
import {getPreference} from '@netx/core-user-next/utils/getPreference';

const logger = getPrefixedLogger('i18n');

// when app is ready make sure we set the user language preference explicitly (for the backend) if not already set
dispatcher.once('app:ready', () => {
	if (i18next.language && getPreference('userLanguage') !== i18next.language) {
		logger.info('sync user language preference:', i18next.language);
		setPreference('userLanguage', i18next.language);
	}
});

/**
 * Initialize i18next
 * @returns i18next instance
 */
export const initializeI18n = () => {
	logger.info('initializing');

	return new Promise((resolve, reject) => {
		// TODO: make external file for easy overriding in things like storybook
		const options: InitOptions<BackendOptions> = {
			// TODO: update translation files to be i18next JSON structure v3
			compatibilityJSON: 'v1',
			interpolation: {
				prefix: '__',
				suffix: '__',
				escapeValue: false,
			},
			backend: backendOptions,
			supportedLngs: I18N_SUPPORTED_LANGUAGES,
			fallbackLng: false,
			// missingKeyNoValueFallbackToKey: true,
			load: 'currentOnly',
			ns: ['translation'],
			defaultNS: 'translation',
			detection: {
				order: ['localStorage', 'navigator', 'htmlTag'],
				lookupLocalStorage: 'locale',
				caches: ['localStorage'],
			},
		};

		i18next
			.use(i18nextXHRBackend)
			.use(i18nextBrowserLanguageDetector)
			.init(options, (error, instance) => {
				// ugh... backbone views
				jqueryI18next.init(i18next, $, {
					// appends $.t = i18next.t
					tName: 't',
					// appends $.i18n = i18next
					i18nName: 'i18n',
					// appends $(selector).localize(opts);
					handleName: 'localize',
					// selector for translating elements
					selectorAttr: 'data-i18n',
					// data-() attribute to grab target element to translate (if diffrent then itself)
					targetAttr: 'i18n-target',
					// data-() attribute that contains options, will load/set if useOptionsAttr = true
					optionsAttr: 'i18n-options',
					// see optionsAttr
					useOptionsAttr: false,
					// parses default values from content ele.val or ele.text
					parseDefaultValueFromContent: true,
				});

				if (error) {
					_setI18nReady();
					_rejectI18nInit();
					logger.error('failed');
					return reject(error);
				}

				// Set on html tag
				document.documentElement.setAttribute('lang', i18next.language);

				_setI18nReady();
				logger.info('initialized:', i18next.language);

				i18next.on('languageChanged', (lng) => {
					logger.info('language changed', lng);
					if (getPreference('userLanguage') !== lng) {
						logger.info('set user language preference', lng);
						setPreference('userLanguage', lng);
					}
					// we currently reload the page on language change - so pointless here
					// document.documentElement.setAttribute('lang', lng);
				});
				resolve(i18next);
				_resolveI18nInit();
			});
	});
};

export default i18next;
